import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { SettingsService } from './core/settings/settings.service';
import { Observable, Subject } from 'rxjs';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { Router } from '@angular/router';
import { AuthService } from '@shared/services/auth/auth.service';
import { InitToken } from '@state/actions/auth/auth.actions';
import { Principal, Token } from '@state/reducers/auth/auth.models';
import { Store } from '@ngrx/store';
import { IState } from '@state/state.module';
import { LoggingService } from '@shared/services/logging/logging.service';
import { IAuthState } from '@state/reducers/auth/auth.reducer';
import { map, filter, takeUntil, tap} from 'rxjs/operators';
import { SignalrService } from '@shared/services/api/dashboard/signalr-service/signalr.service';
import { MessageService } from 'primeng/api';
import { XlsFileUploadedMessage } from '@shared/services/api/dashboard/signalr-service/signalr-model';
import { TranslateService } from '@ngx-translate/core';
declare let $: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

    @HostBinding('class.layout-fixed') get isFixed() { return this.settings.layout.isFixed; }
    @HostBinding('class.layout-boxed') get isBoxed() { return this.settings.layout.isBoxed; }
    @HostBinding('class.layout-fs') get useFullLayout() { return this.settings.layout.useFullLayout; }
    @HostBinding('class.hidden-footer') get hiddenFooter() { return this.settings.layout.hiddenFooter; }
    @HostBinding('class.layout-h') get horizontal() { return this.settings.layout.horizontal; }
    @HostBinding('class.aside-float') get isFloat() { return this.settings.layout.isFloat; }
    @HostBinding('class.aside-toggled') get asideToggled() { return this.settings.layout.asideToggled; }
    private state$: Observable<IAuthState>;
    private destroy$ = new Subject();

    public alreadyConnected: boolean;
    private principal$: Observable<Principal>;
    private principal: Principal;

    constructor(public settings: SettingsService, private authService: AuthService,
        private store: Store<IState>, private oidcSecurityService: OidcSecurityService, private router: Router,
        private signalrService: SignalrService,
        private ms: MessageService,
        private translation: TranslateService,
        private logging: LoggingService) { }

    public ngOnInit() {
        $(document).on('click', '[href="#"]', e => e.preventDefault());
        this.logging.log.info('App component', 'ngOnInit');

        const requiresOidcWorkAround = this.isZoomIntegrationCallBack();
        const isOidcCallbackPage = this.tryGetAuthConfigIdByCallBackUrl();

        // zoom-callback requires amn oidc workaround
        if (requiresOidcWorkAround) {
            // for zoom-callback we enforce oidcService to
            // authenticate on behalf of the 'dashboard-ui' user, that visits the '/events'-route
            this.oidcSecurityService.checkAuth('/events', 'dashboard-ui').subscribe(oidcLoggedIn => {
                this.logging.log.info('Auth: zoom-callback enforced login');
                this.bootstrapAuthState(oidcLoggedIn);
            });
        }
        // oidc-callbacks have precedence over page-reloads
        else if (isOidcCallbackPage) {
            // oidc-callbacks use related configId
            this.logging.log.info('Auth: authConfigId:' +  isOidcCallbackPage);
            this.oidcSecurityService.checkAuth(null, isOidcCallbackPage).subscribe(oidcLoggedIn => {
                this.logging.log.info('Auth: oidc login');
                this.bootstrapAuthState(oidcLoggedIn);
            });
        } else {
            // page reloads use most-recently-used access-token
            this.oidcSecurityService.checkAuthMultiple().subscribe(loginResponses => {
                this.logging.log.info('Auth: page (re)load login');
                const defaultConfig = loginResponses.find(x => x.configId === 'dashboard-ui');
                let mruLoggedIn = this.findMostRecentlyUsedAuth(loginResponses);
                if(!mruLoggedIn) {
                    mruLoggedIn = defaultConfig;
                }
                this.bootstrapAuthState(mruLoggedIn);
            });
        };

        this.state$ = this.store.select(x => x.auth);
        this.state$.pipe(
            takeUntil(this.destroy$),
            filter(x => !!x?.principal?.token?.encoded),
            tap(x => this.principal = x.principal),
            map(x => x.principal.token),
            tap(x => this.logging.log.info('AppComponent: updated token received... Session storage updated.')))
            .subscribe(x => {
                sessionStorage.setItem('mruTenant', x.decoded.ten);
                sessionStorage.setItem('mruLoginHint', x.decoded.sub);
            });

        this.startSignalrJobConnection();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    public startSignalrJobConnection() {
        this.signalrService.initiateJobsConnection()
        .then((connection: any) => {
            connection.on('XlsFileUploaded', (payload: string) => {
                console.log('XlsFileUploaded', payload);
                const message = XlsFileUploadedMessage.fromJson(payload);
                if (message.isForPrincipal(this.principal)) {
                    this.ms.add({
                        severity: 'success',
                        detail: '',
                        key: 'br',
                        life: 5000,
                        sticky: false,
                        closable: true,
                        data: { showFileCenter: true }
                    });
                }
            });
        });
    }

    public navigateToFileCenter(): void {
        this.router.navigate(['/filecenter']);
    }

    private bootstrapAuthState(loginResponse: LoginResponse) {
        this.logging.log.info('Auth: Is Authenticated:', loginResponse.isAuthenticated);
        let accessToken = null;
        if (loginResponse.isAuthenticated) {
            this.oidcSecurityService.getAccessToken(loginResponse.configId).subscribe((at) => {
                accessToken = at;
                this.store.dispatch(new InitToken({ token: accessToken }));
            });
        } else {
            this.authService.clearToken();
            this.store.dispatch(new InitToken({ token: null }));
        }
    }

    private findMostRecentlyUsedAuth(loginResponses: LoginResponse[]): LoginResponse {
        let maxIssuedAt = 0;
        let maxIatLoginResponse = null;
        loginResponses.forEach(lr => {
            if (lr.isAuthenticated && lr.userData.iat > maxIssuedAt) {
                maxIssuedAt = lr.userData.iat;
                maxIatLoginResponse = lr;
            }
        });

        return maxIatLoginResponse;
    }

    private tryGetAuthConfigIdByCallBackUrl(): string {
        const url = new URL(window.location.href);
        if (url.pathname.split('/').includes('support-login-callback')) {
            return 'dashboard-support-ui';
        }
        if (url.pathname.split('/').includes('login-callback')) {
            return 'dashboard-ui';
        }
        return null;
    }

    private isZoomIntegrationCallBack(): boolean {
        const url = new URL(window.location.href);
        if (url.pathname.split('/').includes('callback-zoom')) {
            return true;
        }
        return false;
    }
}
